import { useState, useCallback, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { toast } from "sonner";
import { safeExternalHref } from "../lib/utils";
import { Eye, EyeOff, ArrowLeft, Send, Shield } from "lucide-react";
import api from "../lib/api";

// Dynamic Logo component
const DynamicLogo = () => {
  const { getLogo } = useTheme();
  const logo = getLogo();
  return (
    <div className="w-10 h-10 relative">
      <div className="absolute inset-0 bg-gradient-to-br from-primary to-secondary rounded-full" />
      <div 
        className="relative w-full h-full flex items-center justify-center text-black p-1.5 rounded-full overflow-hidden"
        dangerouslySetInnerHTML={{ __html: logo.svg }}
      />
    </div>
  );
};

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState("");
  const [recaptchaEnabled, setRecaptchaEnabled] = useState(false);
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState("");
  const recaptchaRef = useRef(null);
  const { setUser } = useAuth();
  const { siteContent } = useTheme();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("public/settings").then((r) => {
      setRecaptchaEnabled(!!r.data?.recaptcha_enabled);
      setRecaptchaSiteKey((r.data?.recaptcha_site_key || "").trim());
    }).catch(() => {});
  }, []);

  const handleRecaptchaChange = useCallback((token) => {
    setRecaptchaToken(token || "");
  }, []);

  const handleRecaptchaExpired = useCallback(() => {
    setRecaptchaToken("");
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (recaptchaEnabled && !recaptchaToken) {
      toast.error(language === "tr" ? "Lütfen 'Ben robot değilim' kutusunu işaretleyin." : "Please complete the 'I\'m not a robot' checkbox.");
      return;
    }
    setLoading(true);
    try {
      const response = await api.post("/auth/login", {
        email,
        password,
        ...(recaptchaEnabled && recaptchaToken ? { captcha_token: recaptchaToken } : {})
      });
      const { token, user: userData } = response.data;
      localStorage.setItem("token", token);
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      setUser(userData);
      toast.success(language === "tr" ? "Giriş başarılı!" : "Login successful!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === "tr" ? "Giriş başarısız" : "Login failed"));
      if (recaptchaEnabled && error.response?.status === 400 && recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken("");
      }
    } finally {
      setLoading(false);
    }
  }, [email, password, recaptchaToken, recaptchaEnabled, setUser, language, navigate]);

  return (
    <div className="min-h-screen grid-texture flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between mb-8">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors font-mono text-sm">
            <ArrowLeft className="w-4 h-4" />
            {t("nav.home")}
          </Link>
          <LanguageSwitcher />
        </div>

        <div className="glass p-8">
          <div className="flex items-center gap-2 mb-8">
            <DynamicLogo />
            <div className="sparkle-wrapper">
              <span className="sparkle"></span>
              <span className="sparkle"></span>
              <span className="sparkle"></span>
              <span className="sparkle"></span>
              <span className="glow-credit font-mono font-bold text-xl tracking-normal">ROOT-SEO</span>
            </div>
          </div>

          <h1 className="text-2xl font-mono font-bold mb-2 uppercase">{t("auth.login.title")}</h1>
          <p className="text-muted-foreground mb-8">{t("auth.login.subtitle")}</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="font-mono text-sm uppercase tracking-wider">{t("auth.email")}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@example.com"
                required
                className="bg-black/50 border-white/10 focus:border-primary/50 rounded-none font-mono h-12"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="font-mono text-sm uppercase tracking-wider">{t("auth.password")}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  className="bg-black/50 border-white/10 focus:border-primary/50 rounded-none font-mono h-12 pr-10"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {recaptchaEnabled && recaptchaSiteKey && (
              <div className="flex flex-col items-center gap-2">
                <div className="recaptcha-wrapper flex justify-center [&_.grecaptcha-badge]:!bottom-6 [&_.grecaptcha-badge]:!right-6">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={recaptchaSiteKey}
                    onChange={handleRecaptchaChange}
                    onExpired={handleRecaptchaExpired}
                    theme="dark"
                  />
                </div>
                {!recaptchaToken && (
                  <p className="text-xs text-amber-400/90 font-mono">
                    {language === "tr" ? "Giriş için yukarıdaki kutucuğu işaretlemeniz gerekir." : "You must complete the checkbox above to log in."}
                  </p>
                )}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading || (recaptchaEnabled && !recaptchaToken)}
              className="w-full font-mono uppercase tracking-wider bg-primary text-black hover:bg-primary/90 rounded-none h-12 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="login-submit-btn"
            >
              {loading ? (language === "tr" ? "Giriş yapılıyor..." : "Logging in...") : t("auth.login.button")}
            </Button>
          </form>
          
          {recaptchaEnabled && recaptchaSiteKey && (
            <div className="flex items-center justify-center gap-2 mt-4 text-xs text-muted-foreground">
              <Shield className="w-3 h-3" />
              <span>{language === "tr" ? "reCAPTCHA v2 ile korunuyor" : "Protected by reCAPTCHA v2"}</span>
            </div>
          )}

          <p className="text-center mt-6 text-muted-foreground">
            {t("auth.login.noAccount")}{" "}
            <Link to="/register" className="text-primary hover:underline font-mono" data-testid="go-to-register">
              {t("nav.register")}
            </Link>
          </p>
          
          {/* Telegram Link */}
          {safeExternalHref(siteContent?.footer?.telegramLink) && (
            <div className="text-center mt-4">
              <a 
                href={safeExternalHref(siteContent.footer.telegramLink)} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-primary hover:text-primary/80 font-mono text-sm transition-colors"
              >
                <Send className="w-4 h-4" />
                {language === "tr" ? "Telegram Kanalımız" : "Our Telegram Channel"}
              </a>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="text-center mt-6 space-y-2">
          <p className="text-xs text-muted-foreground font-mono">© ROOT-SEO {t("footer.rights")}</p>
          <div className="sparkle-wrapper inline-block">
            <span className="sparkle"></span>
            <span className="sparkle"></span>
            <span className="sparkle"></span>
            <span className="sparkle"></span>
            <span className="sparkle"></span>
            <span className="sparkle"></span>
            <span className="glow-credit text-xs font-mono font-bold">
              Moderated by AnubisROOT-NoxShell
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
